import { defineStore } from "pinia";

import { getDescendantDuids } from "~/utils/common";

import type { ItemBase } from "./shared";

type State = {
  _listItems: Map<string, ItemBase[]>;
  _draggingItems: {
    group: string;
    category: string;
    items: ItemBase[];
    originalParentDuidMap: Map<string, string | null | undefined>;
    originalCategory: string;
  } | null;
  _dirty: boolean;
  _resetListeners: Map<string, (() => void)[]>;
  _commitListeners: Map<string, (draggedItems: ItemBase[]) => void>;
};

const FIRST_SLOT_BEFORE_RATIO = 0.75;

const makeMapKey = (group: string, category: string) => JSON.stringify([group, category]);

const excludeItems = (items: ItemBase[], excluded: ItemBase[]) =>
  items.filter((item) => !excluded.some((e) => e.duid === item.duid));

const useDragStore = defineStore("DragStore", {
  state: (): State => ({
    _listItems: new Map(),
    _draggingItems: null,
    _dirty: false,
    _resetListeners: new Map(),
    _commitListeners: new Map(),
  }),
  getters: {
    getListItems() {
      return <T extends ItemBase>(group: string, category: string) =>
        (this._listItems.get(makeMapKey(group, category)) ?? []) as T[];
    },
    getDraggingItemsForGroup() {
      return <T extends ItemBase>(group: string) =>
        this._draggingItems?.group === group ? (this._draggingItems.items as T[]) : [];
    },
    getDraggingDescendantItemsForGroup() {
      return <T extends ItemBase>(group: string) => {
        if (this._draggingItems?.group !== group) {
          return [];
        }
        const innerItems = this.getListItems(group, this._draggingItems.originalCategory);
        const descendantDuids = this._draggingItems.items.reduce(
          (acc, item) => new Set([...acc, ...getDescendantDuids(innerItems, item)]),
          new Set<string>()
        );
        const descendants: ItemBase[] = innerItems.filter((e) => descendantDuids.has(e.duid));
        return descendants as T[];
      };
    },
    showPlaceholdersForCategory() {
      return (group: string, category: string): boolean =>
        !!this._draggingItems &&
        this._draggingItems.group === group &&
        this._draggingItems.category !== category &&
        this._draggingItems.originalCategory === category;
    },
  },
  actions: {
    setDraggingItems(group: string, category: string, items: ItemBase[]) {
      this._draggingItems = {
        group,
        category,
        items,
        originalParentDuidMap: new Map(items.map((item) => [item.duid, item.parentDuid])),
        originalCategory: category,
      };
      this._dirty = false;
    },
    finishDrag(group: string) {
      const drag = this._draggingItems;
      if (!drag) {
        return;
      }

      const { items, category } = drag;
      const hasChange =
        this._dirty || items.some((item) => item.parentDuid !== drag.originalParentDuidMap.get(item.duid));
      this._draggingItems = null;
      if (hasChange) {
        this._commitListeners.get(makeMapKey(group, category))?.(items);
      }
    },
    registerCommitListener(group: string, category: string, listener: (draggedItems: ItemBase[]) => void) {
      this._commitListeners.set(makeMapKey(group, category), listener);
    },
    unregisterCommitListener(group: string, category: string, listener: (draggedItems: ItemBase[]) => void) {
      const key = makeMapKey(group, category);
      if (this._commitListeners.get(key) === listener) {
        this._commitListeners.delete(key);
      }
    },
    setListItems(group: string, category: string, items: ItemBase[]) {
      this._listItems.set(makeMapKey(group, category), items);
    },
    moveDraggedItemsToItem(group: string, category: string, targetDuid: string, pointerRatio: number) {
      const drag = this._draggingItems;
      if (!drag || this.getDraggingDescendantItemsForGroup(group).some((e) => e.duid === targetDuid)) {
        return false;
      }

      const targetIndex = excludeItems(this.getListItems(group, category), drag.items).findIndex(
        (item) => item.duid === targetDuid
      );
      if (targetIndex === -1) {
        return false;
      }

      const isBelow = pointerRatio > (targetIndex === 0 ? FIRST_SLOT_BEFORE_RATIO : 0.5);
      this.moveDraggedItemsToIndex(group, category, targetIndex + (isBelow ? 1 : 0));
      return true;
    },
    moveDraggedItemsToIndex(group: string, category: string, index: number) {
      const drag = this._draggingItems;
      let newIndex = index;
      if (!drag) {
        return;
      }
      const isCategoryChange = category !== drag.category;
      const currentItems = this.getListItems(group, category);
      const existingNewItems = excludeItems(currentItems, drag.items);

      if (category !== drag.originalCategory) {
        // Change index to next item after the original index that isn't a child of any other item
        const freeIndex = existingNewItems.findIndex((item, i) => !item.parentDuid && i >= index);
        newIndex = freeIndex === -1 ? existingNewItems.length : freeIndex;
      }

      // Insert items into the new category at the correct index
      const newItems = [...existingNewItems.slice(0, newIndex), ...drag.items, ...existingNewItems.slice(newIndex)];
      if (!isCategoryChange && newItems.every((item, i) => item.duid === currentItems[i]?.duid)) {
        return;
      }

      // Remove items from the old category
      this.setListItems(
        drag.group,
        drag.category,
        excludeItems(this.getListItems(drag.group, drag.category), drag.items)
      );

      this._draggingItems = { ...drag, category };

      if (isCategoryChange) {
        // Revert parentDuid changes if the category has changed
        drag.items.forEach((item) => {
          // eslint-disable-next-line no-param-reassign
          item.parentDuid = drag.originalParentDuidMap.get(item.duid);
        });
      }

      this._dirty = true;
      this.setListItems(group, category, newItems);
    },
    setDraggedItemsParentDuid(parentDuid?: string | null) {
      const drag = this._draggingItems;
      if (!drag || drag.category !== drag.originalCategory) {
        return;
      }

      const firstItemParentDuid = drag.items[0].parentDuid;
      drag.items.forEach((item) => {
        if (
          item.parentDuid !== firstItemParentDuid ||
          item.duid === parentDuid ||
          (item.parentDuid ?? null) === (parentDuid ?? null)
        ) {
          return;
        }

        // eslint-disable-next-line no-param-reassign
        item.parentDuid = parentDuid ?? null;
      });
    },
    registerResetListener(group: string, listener: () => void) {
      const listeners = this._resetListeners.get(group) ?? [];
      listeners.push(listener);
      this._resetListeners.set(group, listeners);
    },
    unregisterResetListener(group: string, listener: () => void) {
      const listeners = this._resetListeners.get(group) ?? [];
      this._resetListeners.set(
        group,
        listeners.filter((e) => e !== listener)
      );
    },
    resetGroup(group: string) {
      const listeners = this._resetListeners.get(group) ?? [];
      listeners.forEach((e) => e());
    },
  },
});

export default useDragStore;
